import { z } from 'zod';

/**
 * external-bank — Pay by Bank (PISP) schema.
 *
 * Two real form fields the user interacts with:
 *   - selectedBank: the bank picked from the modal list.
 *   - termsAccepted: T&C acknowledgement (sent to the BE as
 *     `termsAndConditionsAccepted`, which Volt requires).
 *
 * The `bootstrap` block is server-provided context returned by the
 * `/api/v1/banks` call on mount (banks list + T&C URL). It's modelled here
 * so the resolver doesn't surprise us, but every field is optional at the
 * schema layer — the initial empty state must validate, and the "Continue"
 * CTA is gated by a button-level predicate rather than a schema-level
 * refinement (this is a single-CTA flow, not a multi-field validation flow).
 *
 * The payment params (amount, concept, customer ids, …) are NOT in this
 * response anymore: they're claims of the PISP JWT and are decoded
 * client-side via `decodePispJwt` (see `jwt.ts`).
 */
const bankSchema = z.object({
  id: z.string(),
  name: z.string(),
  country: z.string(),
  // Optional secondary label the BE sends for some banks; shown as the
  // SelectionBox description when present.
  alternativeName: z.string().optional(),
  logoUrl: z.string().optional(),
  // Account-identifier keys Volt requires for this bank (SNAKE_CASE, e.g.
  // `["PSU_ID"]`). Empty/absent for most banks. Each key is rendered as a
  // dynamic input and sent back under `accountIdentifiers` on payment
  // creation (SEB-1664). `additionalInputRequired` is out of scope here.
  inputRequired: z.array(z.string()).optional(),
});

const bootstrapSchema = z.object({
  code: z.string().optional(),
  description: z.string().nullable().optional(),
  banks: z.array(bankSchema).optional(),
  termsUrl: z.string().optional(),
});

export const externalBankSchema = z.object({
  selectedBank: bankSchema.optional(),
  termsAccepted: z.boolean().default(false),
  bootstrap: bootstrapSchema.default({}),
  // Values entered for the selected bank's `inputRequired` keys, keyed by
  // identifier (e.g. `{ PSU_ID: '1234567890' }`). Reset whenever the bank
  // changes; sent verbatim as the `accountIdentifiers` map on payment
  // creation.
  accountIdentifiers: z.record(z.string()).default({}),
});

export type Bank = z.infer<typeof bankSchema>;
export type BootstrapContext = z.infer<typeof bootstrapSchema>;
export type ExternalBankValues = z.infer<typeof externalBankSchema>;

export const externalBankDefaults: ExternalBankValues = {
  selectedBank: undefined,
  termsAccepted: false,
  bootstrap: {},
  accountIdentifiers: {},
};
